import { useState } from 'react';
import Card from './ui/Card';
import Button from './ui/Button';
import { trackPricingToggle, trackSignupClick, trackNavClick } from '../utils/analytics';

const features = {
  free: [
    'Up to 5 debts',
    'Snowball & avalanche strategies',
    '1 budget scenario',
    'Unlimited check-ins',
    'Exact monthly payment schedules',
    'Payoff timeline & roadmap',
  ],
  pro: [
    'Unlimited debts',
    'AI-powered hybrid strategy',
    '3 budget scenarios (floor/target/aggressive)',
    'Scenario comparison with interest & time saved',
    'Revised projections after every check-in',
    'CSV & Excel debt import',
    '5 payoff strategies',
  ],
};

export default function Pricing() {
  const [isAnnual, setIsAnnual] = useState(false);

  return (
    <section id="pricing" className="px-6 py-24 bg-background">
      <div className="max-w-5xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-10">
          <h2 className="text-4xl md:text-5xl font-heading font-bold mb-4 text-fg">
            Simple, Honest Pricing
          </h2>
          <p className="text-lg text-muted max-w-2xl mx-auto">
            Start free. Upgrade when you need advanced features. No hidden fees. No selling your
            data.
          </p>
        </div>

        {/* Toggle */}
        <div className="flex items-center justify-center gap-3 mb-16">
          <div className="relative inline-flex items-center bg-navy/25 rounded-full p-1">
            <button
              onClick={() => {
                setIsAnnual(false);
                trackPricingToggle('monthly');
              }}
              className={`relative z-10 px-5 py-2 text-sm font-semibold rounded-full transition-all duration-200 cursor-pointer ${
                !isAnnual ? 'text-white' : 'text-muted hover:text-fg'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => {
                setIsAnnual(true);
                trackPricingToggle('annual');
              }}
              className={`relative z-10 px-5 py-2 text-sm font-semibold rounded-full transition-all duration-200 cursor-pointer ${
                isAnnual ? 'text-white' : 'text-muted hover:text-fg'
              }`}
            >
              Annual
            </button>
            <div
              className="absolute top-1 bottom-1 rounded-full transition-all duration-200"
              style={{
                background: 'linear-gradient(90deg, #06b6d4 0%, #10b981 100%)',
                left: isAnnual ? '50%' : '4px',
                right: isAnnual ? '4px' : '50%',
              }}
            />
          </div>
          <span className="text-xs font-bold text-white bg-accent px-2.5 py-1 rounded-full">
            Save $30
          </span>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {/* Free Tier */}
          <Card className="relative flex flex-col border-border">
            <div className="mb-6">
              <h3 className="text-2xl font-heading font-bold mb-2 text-fg">Free</h3>
              <div className="flex items-baseline gap-1 mb-2">
                <span className="text-4xl font-heading font-bold text-fg">$0</span>
              </div>
              <p className="text-muted mt-2">Perfect for simple debt portfolios</p>
            </div>

            <ul className="space-y-3 mb-8 flex-1">
              {features.free.map((feature, index) => (
                <li key={index} className="flex items-start gap-3">
                  <svg
                    className="w-5 h-5 text-accent mt-0.5 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2.5}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span className="text-sm text-fg">{feature}</span>
                </li>
              ))}
            </ul>

            <a href="https://app.debtchisel.com/register" className="w-full">
              <Button
                variant="secondary"
                size="lg"
                className="w-full"
                onClick={() => trackSignupClick('free', isAnnual ? 'annual' : 'monthly')}
              >
                Start Free
              </Button>
            </a>
          </Card>

          {/* Pro Tier */}
          <Card className="relative flex flex-col border-accent/50 ring-2 ring-accent/20 shadow-xl">
            <div
              className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 text-white text-xs font-bold rounded-full shadow-lg"
              style={{ background: 'linear-gradient(90deg, #06b6d4 0%, #10b981 100%)' }}
            >
              MOST POPULAR
            </div>

            <div className="mb-6">
              <h3 className="text-2xl font-heading font-bold mb-2 text-fg">Pro</h3>
              <div className="flex items-baseline gap-1 mb-2">
                <span className="text-4xl font-heading font-bold text-fg">
                  {isAnnual ? '$7.50' : '$9.99'}
                </span>
                <span className="text-muted">/month</span>
              </div>
              {isAnnual && (
                <p className="text-sm font-semibold text-accent">$89.99 billed annually</p>
              )}
              <p className="text-muted mt-2">For serious debt elimination</p>
            </div>

            <ul className="space-y-3 mb-8 flex-1">
              {features.pro.map((feature, index) => (
                <li key={index} className="flex items-start gap-3">
                  <svg
                    className="w-5 h-5 text-accent mt-0.5 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2.5}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span className="text-sm text-fg">{feature}</span>
                </li>
              ))}
            </ul>

            <a href="https://app.debtchisel.com/register" className="w-full">
              <Button
                variant="primary"
                size="lg"
                className="w-full"
                onClick={() => trackSignupClick('pro', isAnnual ? 'annual' : 'monthly')}
              >
                Start Free, Upgrade Anytime
              </Button>
            </a>
          </Card>
        </div>

        {/* FAQ Callout */}
        <div className="mt-12 text-center">
          <p className="text-sm text-muted">
            Questions about pricing?{' '}
            <a
              href="#faq"
              className="text-accent hover:underline cursor-pointer"
              onClick={() => trackNavClick('See FAQ below', '#faq')}
            >
              See FAQ below
            </a>
          </p>
        </div>
      </div>
    </section>
  );
}
